<template>
	<CDialog
		:visible="visible"
		custom-class="time-travel-dialog dialog--medium"
		:show-close="submitStatus !== 'validating'"
		:close-on-click-modal="submitStatus !== 'validating'"
		@done="$emit('close')"
	>
		<template #title>
			<div class="flex items-center gap-3">
				<h1 class="dialog__title mt-0 mb-0">Time Travel</h1>
				<CBadge type="error" text="Internal testing" />
			</div>
		</template>

		<div v-if="loading" class="flex justify-center py-5">
			<Inline svg="loading" class="spin" />
		</div>
		<template v-else>
			<CCallout type="info" size="s" class="mb-4">
				Advances this organization's Stripe billing clock to its next stop — the next monthly billing
				boundary, or a dunning deadline (delinquency or forced downgrade) when one comes first — so the
				monthly run, add-on auto top-up, renewal invoicing, and dunning timeline can be simulated against
				the live Stripe sandbox. The metrics below are the ending usage for the month that's closing —
				they're metered to drive the invoice cycle at the next boundary (blank fields carry forward from
				the prior month). Then start or continue time travel. Set steps above 1 to fast forward multiple
				stops in sequence, applying the same metrics to each billing month.
			</CCallout>

			<div v-if="simulatedUtcNow" class="time-travel-dialog__status sz-1 text-3 mb-4">
				Simulated now: <strong>{{ formattedSimulatedUtcNow }}</strong>
				<template v-if="lastMeteredMonth"> · Last billed month: <strong>{{ lastMeteredMonth }}</strong></template>
				<template v-if="delinquentAtDisplay"> · Delinquent at: <strong>{{ delinquentAtDisplay }}</strong></template>
				<template v-if="downgradeAtDisplay"> · Downgrade at: <strong>{{ downgradeAtDisplay }}</strong></template>
			</div>

			<div class="grid b7:grid-cols-2 gap-x-4 b10:gap-x-5 gap-y-3">
				<CInput id="pmso-users" label="Users" type="text" inputmode="numeric" :value="form.Users" @input="v => onChange('Users', v)" />
				<CInput id="pmso-guests" label="Guests" type="text" inputmode="numeric" :value="form.Guests" @input="v => onChange('Guests', v)" />
				<CInput id="pmso-documents" label="Custom Documents" type="text" inputmode="numeric" :value="form.DocumentTemplates" @input="v => onChange('DocumentTemplates', v)" />
				<CInput id="pmso-storage" label="Storage (GB)" type="text" inputmode="decimal" :value="form.StorageGb" @input="v => onChange('StorageGb', v)" />
				<CInput id="pmso-api" label="API Request Segments" type="text" inputmode="numeric" :value="form.ApiRequestSegments" @input="v => onChange('ApiRequestSegments', v)" />
			</div>
		</template>

		<template #footer>
			<div class="time-travel-dialog__action flex flex-col items-end gap-2">
				<div class="flex items-end gap-3">
					<CInput id="pmso-steps" label="Steps" type="text" inputmode="numeric" class="time-travel-dialog__steps" :value="steps" @input="v => steps = v" />
					<StatusButton
						class="button--primary"
						:status="loading ? 'disabled' : submitStatus"
						@click="advance"
						@reset-button-status="submitStatus = 'default'"
					>
						{{ isActive ? 'Continue Time Travel' : 'Start Time Travel' }}
					</StatusButton>
				</div>
				<span v-if="progress" class="time-travel-dialog__progress sz-1 text-3">
					{{ progress }}
				</span>
				<span v-else-if="nextTargetDisplay" class="time-travel-dialog__horizon sz-1 text-3">
					Advances to <strong>{{ nextTargetDisplay }}</strong>
					<template v-if="nextTargetTypeLabel"> ({{ nextTargetTypeLabel }})</template>
				</span>
			</div>
		</template>
	</CDialog>
</template>

<script lang="ts">
	import { defineComponent, reactive, ref, computed, watch } from 'vue';
	import CInput from 'src/components/form-controls/Input.vue';
	import CCallout from 'src/components/content/Callout.vue';
	import CBadge from 'src/components/info/Badge.vue';
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import StatusButton, { type ButtonStatus } from 'src/components/interaction/StatusButton.vue';
	import notify from 'src/utilities/notify';
	import { getPlanStatistics, advancePlanBillingMonth, getBillingSimulationStatus, type PlanStatistics } from '@cognitoforms/api/services/settings/organization-settings-service';

	const BYTES_PER_GB = 1024 ** 3;

	const stopTypeLabels: Record<string, string> = {
		Boundary: 'billing boundary',
		Delinquency: 'delinquency deadline',
		Downgrade: 'forced downgrade deadline',
		PaymentRetry: 'payment retry'
	};

	// Parse a non-negative integer from input text, defaulting to 0.
	const toInt = (value: string): number => {
		const parsed = parseInt(value, 10);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
	};

	// Display bytes as GB, trimming trailing zeros (e.g. 1073741824 -> "1").
	const bytesToGb = (bytes: number): string => parseFloat(((bytes || 0) / BYTES_PER_GB).toFixed(6)).toString();

	// Convert GB input text to whole bytes.
	const gbToBytes = (gb: string): number => {
		const parsed = parseFloat(gb);
		return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * BYTES_PER_GB) : 0;
	};

	// Format a UTC instant as e.g. "Aug 1, 2026, 2:00 AM UTC".
	const formatUtc = (value: string | Date | null): string => {
		if (!value)
			return '';
		const parsed = new Date(value as string);
		if (Number.isNaN(parsed.getTime()))
			return String(value);
		const date = parsed.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
		const time = parsed.toLocaleTimeString('en-US', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit' });
		return `${date}, ${time} UTC`;
	};

	export default defineComponent({
		name: 'PlanStatisticsOverride',
		components: { CInput, CCallout, CBadge, CDialog, StatusButton },
		props: {
			visible: {
				type: Boolean,
				default: false
			}
		},
		emits: ['close', 'billing-advanced'],
		setup(props, { emit }) {
			const loading = ref(false);
			const submitStatus = ref<ButtonStatus>('default');
			const steps = ref('1');
			const progress = ref('');
			const isActive = ref(false);
			const simulatedUtcNow = ref<string | null>(null);
			const nextTargetUtc = ref<string | null>(null);
			const nextTargetType = ref<string | null>(null);
			const delinquentAt = ref<string | null>(null);
			const downgradeAt = ref<string | null>(null);
			const lastMeteredMonth = ref<string | null>(null);
			const form = reactive({
				Users: '',
				Guests: '',
				DocumentTemplates: '',
				StorageGb: '',
				ApiRequestSegments: ''
			});

			const formattedSimulatedUtcNow = computed(() => formatUtc(simulatedUtcNow.value));
			const nextTargetDisplay = computed(() => formatUtc(nextTargetUtc.value));
			const nextTargetTypeLabel = computed(() => nextTargetType.value ? stopTypeLabels[nextTargetType.value] ?? null : null);
			const delinquentAtDisplay = computed(() => formatUtc(delinquentAt.value));
			const downgradeAtDisplay = computed(() => formatUtc(downgradeAt.value));

			const applyForm = (stats: PlanStatistics) => {
				form.Users = (stats.Users ?? 0).toString();
				form.Guests = (stats.Guests ?? 0).toString();
				form.DocumentTemplates = (stats.DocumentTemplates ?? 0).toString();
				form.StorageGb = bytesToGb(stats.StorageBytes);
				form.ApiRequestSegments = (stats.ApiRequestSegments ?? 0).toString();
			};

			const load = async () => {
				loading.value = true;
				submitStatus.value = 'default';
				try {
					const [status, stats] = await Promise.all([getBillingSimulationStatus(), getPlanStatistics()]);
					isActive.value = status.IsActive;
					simulatedUtcNow.value = status.SimulatedUtcNow ? String(status.SimulatedUtcNow) : null;
					nextTargetUtc.value = status.NextTargetUtc ? String(status.NextTargetUtc) : null;
					nextTargetType.value = status.NextTargetType ?? null;
					delinquentAt.value = status.DelinquentAt ? String(status.DelinquentAt) : null;
					downgradeAt.value = status.DowngradeAt ? String(status.DowngradeAt) : null;
					applyForm(stats);
				}
				catch {
					notify('Failed to load time travel status.', 'error');
				}
				loading.value = false;
			};

			watch(() => props.visible, visible => {
				if (visible)
					load();
			}, { immediate: true });

			const onChange = (field: keyof typeof form, value: string) => {
				form[field] = value;
			};

			const advance = async () => {
				if (submitStatus.value === 'validating')
					return;
				const totalSteps = Math.min(Math.max(toInt(steps.value), 1), 24);
				steps.value = totalSteps.toString();
				let completedSteps = 0;
				submitStatus.value = 'validating';
				try {
					while (completedSteps < totalSteps) {
						if (totalSteps > 1)
							progress.value = `Advancing step ${completedSteps + 1} of ${totalSteps}…`;
						const result = await advancePlanBillingMonth({
							Month: '',
							Users: toInt(form.Users),
							Guests: toInt(form.Guests),
							DocumentTemplates: toInt(form.DocumentTemplates),
							StorageBytes: gbToBytes(form.StorageGb),
							ApiRequestSegments: toInt(form.ApiRequestSegments)
						});
						completedSteps++;
						simulatedUtcNow.value = result.SimulatedUtcNow ? String(result.SimulatedUtcNow) : null;
						nextTargetUtc.value = result.NextTargetUtc ? String(result.NextTargetUtc) : null;
						nextTargetType.value = result.NextTargetType ?? null;
						delinquentAt.value = result.DelinquentAt ? String(result.DelinquentAt) : null;
						downgradeAt.value = result.DowngradeAt ? String(result.DowngradeAt) : null;
						if (result.MeteredMonth)
							lastMeteredMonth.value = result.MeteredMonth;
						isActive.value = true;
					}
					submitStatus.value = 'default';
					const billedSuffix = lastMeteredMonth.value ? `; last billed ${lastMeteredMonth.value}` : '';
					notify(`Advanced billing clock ${totalSteps > 1 ? `${totalSteps} steps ` : ''}to ${formattedSimulatedUtcNow.value}${billedSuffix}.`, 'success');
					emit('billing-advanced');
					emit('close');
				}
				catch {
					submitStatus.value = 'error';
					const failedAfter = completedSteps > 0 ? ` after advancing ${completedSteps} of ${totalSteps} steps` : '';
					notify(`Failed to advance time travel${failedAfter}. If this Stripe account has Billing Automations configured, an existing customer cannot be attached to a test clock.`, 'error');
					if (completedSteps > 0)
						emit('billing-advanced');
				}
				progress.value = '';
			};

			return { loading, submitStatus, steps, progress, isActive, simulatedUtcNow, nextTargetUtc, nextTargetType, nextTargetTypeLabel, delinquentAt, downgradeAt, delinquentAtDisplay, downgradeAtDisplay, lastMeteredMonth, form, formattedSimulatedUtcNow, nextTargetDisplay, onChange, advance };
		}
	});
</script>

<style lang="scss">
.time-travel-dialog {

	&__steps {
		width: 5.5rem;
	}
}
</style>
